import { z } from 'zod'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { InvoiceRepository } from '../../application/InvoiceRepository'
import type { ClientCreditEntry, InvoiceItem, PaymentRecord } from '../../domain/invoice'
import {
  creditStatuses,
  effectiveInvoiceStatus,
  torontoToday,
  type InvoiceDetailResult,
  type InvoicePage,
  type InvoiceRecord,
  type InvoiceStatus,
  type CreditNote,
  type OpenInvoiceOption,
  type PaymentListItem,
  type PaymentPage,
} from '../../domain/invoice'
import { displayClientName, legacyInvoiceRowSchema, mapLegacyInvoice } from './legacyInvoiceMapper'
import { reportUnexpectedError } from '@/shared/infrastructure/observability/reportUnexpectedError'
import { splitSearchTokens } from '@/shared/domain/searchTokens'
import { findClientIdsBySearch } from '@/infrastructure/supabase/partySearch'

const invoiceColumns =
  'id,numero,client_id,contrat_id,date,sous_total,tps,tvq,total,solde,statut,' +
  'issued_at,sent_at,cancelled_at,cancellation_reason,created_at,updated_at,deleted_at,' +
  'client:clients(numero,prenom,nom,entreprise),contrat:contracts(numero,statut,status_v2)'

/** Variante pour filtrer sur l'état du Contrat lié : la jointure devient obligatoire (`!inner`). */
const invoiceColumnsWithContract = invoiceColumns.replace('contrat:contracts(', 'contrat:contracts!inner(')

/**
 * Paiements valides et crédits imputés, par Facture, pour les lignes d'une page. Mêmes déductions que
 * `recalculate_invoice_v1` : notes de crédit `APPLIED`, crédit au compte (`APPLICATION`, montants
 * négatifs), paiements `RECORDED`. Une lecture refusée ou en erreur laisse la valeur absente — l'écran
 * affiche « — » plutôt qu'un montant reconstitué.
 */
async function invoiceSettlements(
  client: SupabaseClient,
  invoiceIds: readonly string[],
): Promise<{ paid: Map<string, number> | null; credited: Map<string, number> | null }> {
  if (invoiceIds.length === 0) return { paid: new Map(), credited: new Map() }
  const ids = [...invoiceIds]
  const [payments, notes, account] = await Promise.all([
    client
      .from('payments')
      .select('facture_id,montant')
      .in('facture_id', ids)
      .eq('statut', 'RECORDED')
      .is('deleted_at', null),
    client
      .from('credit_notes')
      .select('invoice_id,amount_cents')
      .in('invoice_id', ids)
      .eq('statut', 'APPLIED'),
    client
      .from('client_credit_entries')
      .select('invoice_id,amount_cents')
      .in('invoice_id', ids)
      .eq('kind', 'APPLICATION'),
  ])

  let paid: Map<string, number> | null = null
  if (!payments.error) {
    paid = new Map(ids.map((id) => [id, 0]))
    for (const row of payments.data as { facture_id: string; montant: number | string }[]) {
      paid.set(row.facture_id, (paid.get(row.facture_id) ?? 0) + Math.round(Number(row.montant) * 100))
    }
  }
  let credited: Map<string, number> | null = null
  if (!notes.error && !account.error) {
    credited = new Map(ids.map((id) => [id, 0]))
    for (const row of notes.data as { invoice_id: string; amount_cents: number | string }[]) {
      credited.set(row.invoice_id, (credited.get(row.invoice_id) ?? 0) + Number(row.amount_cents))
    }
    // Une imputation au compte est un mouvement négatif du compte client : elle crédite la Facture.
    for (const row of account.data as { invoice_id: string; amount_cents: number | string }[]) {
      credited.set(row.invoice_id, (credited.get(row.invoice_id) ?? 0) - Number(row.amount_cents))
    }
  }
  return { paid, credited }
}

/** Statuts V3 qui possèdent une correspondance legacy filtrable. */
const legacyStatus: Partial<Record<InvoiceStatus, string[]>> = {
  DRAFT: ['brouillon'],
  ISSUED: ['envoyee'],
  SENT: ['envoyee'],
  PARTIALLY_PAID: ['partiellement_payee'],
  PAID: ['payee'],
  OVERDUE: ['en_retard'],
  CANCELLED: ['annulee'],
}

const itemRowSchema = z.object({
  id: z.uuid(),
  description: z.string(),
  quantity_milli: z.number().int(),
  unit_price_cents: z.number().int(),
  subtotal_cents: z.number().int(),
  taxable: z.boolean(),
  ordre: z.number().int(),
})

const relatedClientSchema = z.object({
  prenom: z.string().nullable(),
  nom: z.string().nullable(),
  entreprise: z.string().nullable(),
})

const paymentInvoiceColumns = 'facture:invoices(numero,client:clients(prenom,nom,entreprise))'

const paymentListRowSchema = z.object({
  id: z.uuid(),
  montant: z.coerce.number(),
  methode: z.enum(['CASH', 'CHEQUE', 'E_TRANSFER', 'CARD', 'ONLINE', 'OTHER']).nullable(),
  reference: z.string().nullable(),
  date: z.string(),
  statut: z.enum(['RECORDED', 'CANCELLED']),
  cancellation_reason: z.string().nullable(),
  facture_id: z.uuid(),
  facture: z.object({ numero: z.string().nullable(), client: relatedClientSchema.nullable() }).nullable(),
})

const creditNoteRowSchema = z.object({
  id: z.uuid(),
  numero: z.string(),
  invoice_id: z.uuid(),
  amount_cents: z.number().int(),
  reason: z.string(),
  statut: z.enum(creditStatuses),
  issued_at: z.string().nullable(),
  applied_at: z.string().nullable(),
  cancellation_reason: z.string().nullable(),
})

const openInvoiceRowSchema = z.object({
  id: z.uuid(),
  numero: z.string(),
  solde: z.coerce.number(),
  client: relatedClientSchema.nullable(),
})

// La base rend des montants numeric : PostgREST les sérialise en chaîne.
const replaceItemsResultSchema = z.object({
  invoiceId: z.uuid(),
  items: z.number().int(),
  subtotal: z.coerce.number(),
  total: z.coerce.number(),
  updatedAt: z.string(),
})

const paymentRowSchema = z.object({
  id: z.uuid(),
  montant: z.coerce.number(),
  methode: z.enum(['CASH', 'CHEQUE', 'E_TRANSFER', 'CARD', 'ONLINE', 'OTHER']).nullable(),
  reference: z.string().nullable(),
  date: z.string(),
  statut: z.enum(['RECORDED', 'CANCELLED']),
  cancellation_reason: z.string().nullable(),
})

const recordPaymentResultSchema = z.object({
  paymentId: z.uuid(),
  invoiceId: z.uuid(),
  number: z.string(),
  amountCents: z.coerce.number().int(),
  balanceCents: z.coerce.number().int(),
  invoiceStatus: z.string(),
  creditedCents: z.coerce.number().int().optional(),
  accountBalanceCents: z.coerce.number().int().optional(),
  replayed: z.boolean(),
})

const creditEntryRowSchema = z.object({
  id: z.uuid(),
  amount_cents: z.coerce.number().int(),
  kind: z.enum(['OVERPAYMENT', 'APPLICATION', 'REVERSAL']),
  reason: z.string(),
  invoice_id: z.uuid().nullable(),
  created_at: z.string(),
})

const applyCreditResultSchema = z.object({
  entryId: z.uuid(),
  invoiceId: z.uuid(),
  number: z.string(),
  amountCents: z.coerce.number().int(),
  balanceCents: z.coerce.number().int(),
  accountBalanceCents: z.coerce.number().int(),
  replayed: z.boolean(),
})

// Sprint H : chaque champ est `null` quand `finance_billing_kpis_v1` juge le
// rôle courant non autorisé pour ce groupe (`invoice.read`/`payment.read`) —
// jamais un zéro, qui laisserait croire à un solde nul.
const billingKpisRowSchema = z.object({
  to_collect_cents: z.coerce.number().int().nullable(),
  overdue_cents: z.coerce.number().int().nullable(),
  collected_this_month_cents: z.coerce.number().int().nullable(),
  open_invoices_count: z.coerce.number().int().nullable(),
  vip_total_cents: z.coerce.number().int().nullable(),
  vip_count: z.coerce.number().int().nullable(),
})

const issueResultSchema = z.object({
  invoiceId: z.uuid(),
  number: z.string().min(1),
  status: z.literal('ISSUED'),
  issuedAt: z.string(),
})

function statistics(items: InvoiceRecord[]): InvoicePage['stats'] {
  const effective = items.map((invoice) => effectiveInvoiceStatus(invoice))
  return {
    total: items.length,
    draft: effective.filter((status) => status === 'DRAFT').length,
    issued: effective.filter((status) => status === 'ISSUED' || status === 'SENT').length,
    partiallyPaid: effective.filter((status) => status === 'PARTIALLY_PAID').length,
    paid: effective.filter((status) => status === 'PAID').length,
    overdue: effective.filter((status) => status === 'OVERDUE').length,
    cancelled: effective.filter((status) => status === 'CANCELLED').length,
    // Une facture annulée ne se réclame plus : elle sort du solde dû.
    outstandingCents: items.reduce(
      (sum, invoice, index) =>
        effective[index] === 'CANCELLED' ? sum : sum + Math.max(invoice.balanceCents, 0),
      0,
    ),
  }
}

/**
 * Numéro de facture, ou Client par son nom, téléphone, courriel ou adresse :
 * la facture ne porte pas ces champs, on retrouve d'abord les Clients.
 */
async function invoiceSearchFilter(client: SupabaseClient, search: string): Promise<string> {
  const byClient = await findClientIdsBySearch(client, splitSearchTokens(search))
  return [
    `numero.ilike.%${search}%`,
    ...(byClient.length > 0 ? [`client_id.in.(${byClient.join(',')})`] : []),
  ].join(',')
}

function sanitize(value: string): string {
  return value
    .replace(/[%_,().'"\\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function createSupabaseInvoiceRepository(
  client: SupabaseClient,
  supabaseUrl: string,
  anonKey: string,
): InvoiceRepository {
  return {
    async list(filters): Promise<InvoicePage> {
      const from = (filters.page - 1) * filters.pageSize
      let query = client
        .from('invoices')
        .select(filters.contractStatus ? invoiceColumnsWithContract : invoiceColumns, { count: 'exact' })
        .is('deleted_at', null)
        .range(from, from + filters.pageSize - 1)

      // Sprint H, onglet « À encaisser » : échéance croissante (en retard
      // d'abord); sinon l'ordre habituel, création la plus récente d'abord.
      query =
        filters.sort === 'dueAsc'
          ? query.order('date', { ascending: true })
          : query.order('created_at', { ascending: false })

      if (filters.clientId) {
        query = query.eq('client_id', filters.clientId)
      }
      if (filters.contractId) {
        query = query.eq('contrat_id', filters.contractId)
      }
      if (filters.contractStatus) {
        query = query.eq('contrat.status_v2', filters.contractStatus)
      }
      if (filters.overdueOnly) {
        query = query
          .in('statut', ['envoyee', 'partiellement_payee', 'en_retard'])
          .gt('solde', 0)
          .lt('date', torontoToday())
      }
      if (filters.openOnly) {
        // Émise, ni annulée ni brouillon, et solde encore dû — jamais un
        // filtre appliqué après coup sur une liste déjà chargée.
        query = query.in('statut', ['envoyee', 'partiellement_payee', 'en_retard']).gt('solde', 0)
      }
      if (filters.status) {
        const legacy = legacyStatus[filters.status]
        // `READY` et `ARCHIVED` n'existent pas au registre legacy : aucun
        // résultat plutôt qu'un filtre silencieusement ignoré.
        if (!legacy) {
          return {
            items: [],
            page: filters.page,
            pageSize: filters.pageSize,
            total: 0,
            stats: statistics([]),
            meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
          }
        }
        query = query.in('statut', legacy)
      }
      if (filters.search) {
        const search = sanitize(filters.search)
        if (search) query = query.or(await invoiceSearchFilter(client, search))
      }

      const response = await query
      if (response.error) throw new Error(response.error.message)

      const items: InvoiceRecord[] = []
      for (const raw of response.data) {
        const parsed = legacyInvoiceRowSchema.safeParse(raw)
        // Une facture illisible est écartée du décompte plutôt que montrée avec
        // des montants approximatifs.
        if (!parsed.success) continue
        items.push(mapLegacyInvoice(parsed.data))
      }

      // Décomptes (une ligne demandée) : pas de lecture des règlements.
      if (filters.pageSize > 1) {
        const { paid, credited } = await invoiceSettlements(
          client,
          items.map((invoice) => invoice.id),
        )
        for (const invoice of items) {
          const paidCents = paid?.get(invoice.id)
          const creditedCents = credited?.get(invoice.id)
          if (paidCents !== undefined) invoice.paidCents = paidCents
          if (creditedCents !== undefined) invoice.creditedCents = creditedCents
        }
      }

      return {
        items,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? items.length,
        stats: statistics(items),
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },

    async getByReference(reference): Promise<InvoiceDetailResult | null> {
      const response = await client
        .from('invoices')
        .select(invoiceColumns)
        .eq('numero', reference)
        .is('deleted_at', null)
        .maybeSingle()
      if (response.error) throw new Error(response.error.message)
      if (!response.data) return null
      const parsed = legacyInvoiceRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('INVOICE_ROW_INVALID')
      return {
        invoice: mapLegacyInvoice(parsed.data),
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },

    async issue(command) {
      const response = (await client.rpc('issue_invoice_v1', { p_command: command })) as {
        data?: unknown
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'INVOICE_ISSUE_FAILED')
      const parsed = issueResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('INVOICE_ISSUE_RESPONSE_INVALID')
      return parsed.data
    },

    async cancel(command) {
      const response = (await client.rpc('cancel_invoice_v1', { p_command: command })) as {
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'INVOICE_CANCEL_FAILED')
    },
    async listPayments(invoiceId) {
      const response = await client
        .from('payments')
        .select('id,montant,methode,reference,date,statut,cancellation_reason')
        .eq('facture_id', invoiceId)
        .is('deleted_at', null)
        .order('date', { ascending: false })
      if (response.error) throw new Error(response.error.message)
      const payments: PaymentRecord[] = []
      for (const raw of response.data) {
        const parsed = paymentRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        payments.push({
          id: row.id,
          amountCents: Math.round(row.montant * 100),
          ...(row.methode ? { method: row.methode } : {}),
          ...(row.reference ? { reference: row.reference } : {}),
          receivedOn: row.date,
          status: row.statut,
          ...(row.cancellation_reason ? { cancellationReason: row.cancellation_reason } : {}),
        })
      }
      return payments
    },

    async listRecentPayments(filters): Promise<PaymentPage> {
      const empty = (): PaymentPage => ({
        items: [],
        page: filters.page,
        pageSize: filters.pageSize,
        total: 0,
        recorded: 0,
        cancelled: 0,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      })

      // La recherche porte sur ce que l'écran montre (client ou numéro de facture) : elle se résout
      // d'abord en Factures, puis filtre les paiements côté serveur — toutes pages confondues.
      let invoiceIds: string[] | null = null
      const search = sanitize(filters.search)
      if (search) {
        const matches = await client
          .from('invoices')
          .select('id')
          .is('deleted_at', null)
          .or(await invoiceSearchFilter(client, search))
          .limit(1000)
        if (matches.error) throw new Error(matches.error.message)
        invoiceIds = (matches.data as { id: string }[]).map((row) => row.id)
        if (invoiceIds.length === 0) return empty()
      }

      const since =
        filters.windowDays === null
          ? null
          : new Date(Date.now() - filters.windowDays * 86400000).toISOString().slice(0, 10)
      const from = (filters.page - 1) * filters.pageSize
      let pageQuery = client
        .from('payments')
        .select(
          'id,montant,methode,reference,date,statut,cancellation_reason,facture_id,' + paymentInvoiceColumns,
          {
            count: 'exact',
          },
        )
        .is('deleted_at', null)
      if (since) pageQuery = pageQuery.gte('date', since)
      if (invoiceIds) pageQuery = pageQuery.in('facture_id', invoiceIds)
      if (filters.status !== 'ALL') pageQuery = pageQuery.eq('statut', filters.status)
      const countBy = (status: 'RECORDED' | 'CANCELLED') => {
        let countQuery = client
          .from('payments')
          .select('id', { count: 'exact', head: true })
          .is('deleted_at', null)
          .eq('statut', status)
        if (since) countQuery = countQuery.gte('date', since)
        if (invoiceIds) countQuery = countQuery.in('facture_id', invoiceIds)
        return countQuery
      }

      const [response, recorded, cancelled] = await Promise.all([
        pageQuery.order('date', { ascending: false }).range(from, from + filters.pageSize - 1),
        countBy('RECORDED'),
        countBy('CANCELLED'),
      ])
      if (response.error) throw new Error(response.error.message)
      if (recorded.error) throw new Error(recorded.error.message)
      if (cancelled.error) throw new Error(cancelled.error.message)

      const items: PaymentListItem[] = []
      for (const raw of response.data) {
        const parsed = paymentListRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        items.push({
          id: row.id,
          amountCents: Math.round(row.montant * 100),
          ...(row.methode ? { method: row.methode } : {}),
          ...(row.reference ? { reference: row.reference } : {}),
          receivedOn: row.date,
          status: row.statut,
          ...(row.cancellation_reason ? { cancellationReason: row.cancellation_reason } : {}),
          invoiceId: row.facture_id,
          invoiceNumber: row.facture?.numero ?? '',
          clientName: displayClientName(row.facture?.client),
        })
      }

      return {
        items,
        page: filters.page,
        pageSize: filters.pageSize,
        total: response.count ?? items.length,
        recorded: recorded.count ?? 0,
        cancelled: cancelled.count ?? 0,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },

    async searchOpenInvoices(term): Promise<OpenInvoiceOption[]> {
      let query = client
        .from('invoices')
        .select('id,numero,solde,statut,client:clients(prenom,nom,entreprise)')
        .is('deleted_at', null)
        // Ni brouillon ni annulée : on n'encaisse que ce qui est réclamé.
        .in('statut', ['envoyee', 'partiellement_payee', 'en_retard'])
        .gt('solde', 0)
        .order('date', { ascending: true })
        .limit(20)

      const search = sanitize(term)
      if (search) query = query.or(await invoiceSearchFilter(client, search))

      const response = await query
      if (response.error) throw new Error(response.error.message)
      const options: OpenInvoiceOption[] = []
      for (const raw of response.data) {
        const parsed = openInvoiceRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        options.push({
          id: row.id,
          number: row.numero,
          clientName: displayClientName(row.client),
          balanceCents: Math.round(row.solde * 100),
        })
      }
      return options
    },

    async sendCreditNote(command) {
      const session = await client.auth.getSession()
      const token = session.data.session?.access_token
      if (!token) throw new Error('UNAUTHENTICATED')
      const response = await fetch(`${supabaseUrl}/functions/v1/send-credit-note`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(command),
      })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok) throw new Error('CREDIT_NOTE_SEND_FAILED')
      const parsed = z.object({ deliveryId: z.uuid() }).safeParse(body)
      if (!parsed.success) throw new Error('CREDIT_NOTE_SEND_RESPONSE_INVALID')
      return parsed.data
    },

    async sendReceipt(command) {
      // Comme l'envoi d'une Facture : le PDF est produit et déposé côté serveur.
      const session = await client.auth.getSession()
      const token = session.data.session?.access_token
      if (!token) throw new Error('UNAUTHENTICATED')
      const response = await fetch(`${supabaseUrl}/functions/v1/send-receipt`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(command),
      })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok) throw new Error('RECEIPT_SEND_FAILED')
      const parsed = z.object({ receiptId: z.uuid() }).safeParse(body)
      if (!parsed.success) throw new Error('RECEIPT_SEND_RESPONSE_INVALID')
      return parsed.data
    },

    async listCreditNotes(invoiceId): Promise<CreditNote[]> {
      const response = await client
        .from('credit_notes')
        .select('id,numero,invoice_id,amount_cents,reason,statut,issued_at,applied_at,cancellation_reason')
        .eq('invoice_id', invoiceId)
        .order('created_at', { ascending: false })
      if (response.error) throw new Error(response.error.message)
      const notes: CreditNote[] = []
      for (const raw of response.data) {
        const parsed = creditNoteRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        notes.push({
          id: row.id,
          number: row.numero,
          invoiceId: row.invoice_id,
          amountCents: row.amount_cents,
          reason: row.reason,
          status: row.statut,
          ...(row.issued_at ? { issuedAt: row.issued_at } : {}),
          ...(row.applied_at ? { appliedAt: row.applied_at } : {}),
          ...(row.cancellation_reason ? { cancellationReason: row.cancellation_reason } : {}),
        })
      }
      return notes
    },

    async createCreditNote(command) {
      const response = (await client.rpc('create_credit_note_v1', { p_command: command })) as {
        data: unknown
        error: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'CREDIT_CREATE_FAILED')
      const parsed = z.object({ creditNoteId: z.uuid(), number: z.string().min(1) }).safeParse(response.data)
      if (!parsed.success) throw new Error('CREDIT_CREATE_RESPONSE_INVALID')
      return parsed.data
    },

    async transitionCreditNote(command) {
      const response = (await client.rpc('transition_credit_note_v1', { p_command: command })) as {
        data: unknown
        error: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'CREDIT_TRANSITION_FAILED')
      const parsed = z.object({ status: z.string() }).safeParse(response.data)
      if (!parsed.success) throw new Error('CREDIT_TRANSITION_RESPONSE_INVALID')
      return parsed.data
    },

    async recordPayment(command) {
      const response = (await client.rpc('record_payment_v2', { p_command: command })) as {
        data?: unknown
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'PAYMENT_RECORD_FAILED')
      const parsed = recordPaymentResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('PAYMENT_RECORD_RESPONSE_INVALID')
      try {
        const session = await client.auth.getSession()
        const token = session.data.session?.access_token
        if (token) {
          const communicationResponse = await fetch(
            `${supabaseUrl}/functions/v1/process-payment-communications`,
            {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                apikey: anonKey,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ paymentId: parsed.data.paymentId }),
            },
          )
          if (!communicationResponse.ok) throw new Error('PAYMENT_COMMUNICATION_PROCESSING_FAILED')
        }
      } catch (error) {
        // L'encaissement est déjà acquis. La file FAILED permet une reprise;
        // une panne de courriel ne doit jamais faire croire que le paiement a échoué.
        reportUnexpectedError(error, {
          operation: 'process_payment_communications',
          paymentId: parsed.data.paymentId,
        })
      }
      // Un serveur antérieur au compte de crédit ne rend pas ces champs :
      // absents, ils le restent plutôt que d'être rendus à zéro.
      const { creditedCents, accountBalanceCents, ...rest } = parsed.data
      return {
        ...rest,
        ...(creditedCents === undefined ? {} : { creditedCents }),
        ...(accountBalanceCents === undefined ? {} : { accountBalanceCents }),
      }
    },

    async getClientCreditAccount(clientId) {
      // Le solde n'est pas relu depuis une colonne : il est la somme des
      // mouvements, calculée par la même fonction que le serveur emploie.
      const [entriesResponse, balanceResponse] = await Promise.all([
        client
          .from('client_credit_entries')
          .select('id,amount_cents,kind,reason,invoice_id,created_at')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false }),
        client.rpc('client_credit_balance_v1', { p_client_id: clientId }),
      ])
      if (entriesResponse.error) throw new Error(entriesResponse.error.message)
      if (balanceResponse.error) throw new Error(balanceResponse.error.message)
      const entries: ClientCreditEntry[] = []
      for (const raw of entriesResponse.data) {
        const parsed = creditEntryRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        entries.push({
          id: row.id,
          amountCents: row.amount_cents,
          kind: row.kind,
          reason: row.reason,
          ...(row.invoice_id ? { invoiceId: row.invoice_id } : {}),
          createdAt: row.created_at,
        })
      }
      return {
        clientId,
        balanceCents: z.coerce.number().int().parse(balanceResponse.data),
        entries,
      }
    },

    async applyClientCredit(command) {
      const response = (await client.rpc('apply_client_credit_v1', { p_command: command })) as {
        data?: unknown
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'CREDIT_APPLY_FAILED')
      const parsed = applyCreditResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CREDIT_APPLY_RESPONSE_INVALID')
      return parsed.data
    },

    async cancelPayment(command) {
      const response = (await client.rpc('cancel_payment_v1', { p_command: command })) as {
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'PAYMENT_CANCEL_FAILED')
    },
    async listItems(invoiceId) {
      const response = await client
        .from('invoice_items')
        .select('id,description,quantity_milli,unit_price_cents,subtotal_cents,taxable,ordre')
        .eq('invoice_id', invoiceId)
        .order('ordre', { ascending: true })
      if (response.error) throw new Error(response.error.message)
      const items: InvoiceItem[] = []
      for (const raw of response.data) {
        const parsed = itemRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        items.push({
          id: row.id,
          description: row.description,
          quantityMilli: row.quantity_milli,
          unitPriceCents: row.unit_price_cents,
          subtotalCents: row.subtotal_cents,
          taxable: row.taxable,
          order: row.ordre,
        })
      }
      return items
    },
    async replaceItems(command) {
      const response = (await client.rpc('replace_invoice_items_v1', { p_command: command })) as {
        data: unknown
        error: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'INVOICE_ITEMS_REPLACE_FAILED')
      const parsed = replaceItemsResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('INVOICE_ITEMS_REPLACE_RESPONSE_INVALID')
      return parsed.data
    },
    async send(command) {
      // L'envoi passe par l'Edge Function : le PDF est produit et déposé côté
      // serveur, jamais confié au navigateur.
      const session = await client.auth.getSession()
      const token = session.data.session?.access_token
      if (!token) throw new Error('UNAUTHENTICATED')
      const response = await fetch(`${supabaseUrl}/functions/v1/send-invoice`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          apikey: anonKey,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(command),
      })
      const body: unknown = await response.json().catch(() => null)
      if (!response.ok) throw new Error('INVOICE_SEND_FAILED')
      const parsed = z.object({ number: z.string() }).safeParse(body)
      if (!parsed.success) throw new Error('INVOICE_SEND_RESPONSE_INVALID')
      return parsed.data
    },

    async getBillingKpis() {
      const response = (await client.rpc('finance_billing_kpis_v1')) as {
        data?: unknown
        error?: { message?: string } | null
      }
      if (response.error) throw new Error(response.error.message ?? 'BILLING_KPIS_FAILED')
      const parsed = billingKpisRowSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('BILLING_KPIS_RESPONSE_INVALID')
      const row = parsed.data
      return {
        toCollectCents: row.to_collect_cents,
        overdueCents: row.overdue_cents,
        collectedThisMonthCents: row.collected_this_month_cents,
        openInvoicesCount: row.open_invoices_count,
        vipTotalCents: row.vip_total_cents,
        vipCount: row.vip_count,
        meta: { source: 'supabase', retrievedAt: new Date().toISOString() },
      }
    },

    async listPaymentsForClient(clientId, limit = 10) {
      const response = await client
        .from('payments')
        .select(
          'id,montant,methode,reference,date,statut,cancellation_reason,facture_id,' +
            'facture:invoices!inner(numero,client_id,client:clients(prenom,nom,entreprise))',
        )
        .eq('facture.client_id', clientId)
        .is('deleted_at', null)
        .order('date', { ascending: false })
        .limit(limit)
      if (response.error) throw new Error(response.error.message)
      const items: PaymentListItem[] = []
      for (const raw of response.data) {
        const parsed = paymentListRowSchema.safeParse(raw)
        if (!parsed.success) continue
        const row = parsed.data
        items.push({
          id: row.id,
          amountCents: Math.round(row.montant * 100),
          ...(row.methode ? { method: row.methode } : {}),
          ...(row.reference ? { reference: row.reference } : {}),
          receivedOn: row.date,
          status: row.statut,
          ...(row.cancellation_reason ? { cancellationReason: row.cancellation_reason } : {}),
          invoiceId: row.facture_id,
          invoiceNumber: row.facture?.numero ?? '',
          clientName: displayClientName(row.facture?.client),
        })
      }
      return items
    },
  }
}
